/*
  Shows the line total for each add-on as the quantity selector moves.

  Add-ons are added at the parent's quantity (see the items[] branch in
  product-form.js), so at quantity 2 a $50 fitment costs $100. The tick box
  showing a bare "$50.00" in that state is a nasty surprise at checkout.

  Liquid renders the unit price, so the correct figure is on the page before
  this runs and stays correct without JS. This only takes over once the quantity
  is above 1.

  Money is formatted against the shop's own money_format string rather than
  Intl, so totals match the money filter used everywhere else in the theme -
  including the separator and decimal conventions of non-en-AU currencies.

  Listens on the document rather than binding to the quantity input directly,
  because product-info.js replaces the whole info column when a variant changes,
  which would take any directly bound listeners with it.
*/

(function () {
  const QUANTITY_SELECTOR = 'input[name="quantity"]';
  const PRICE_SELECTOR = '[data-addon-price]';
  const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';

    const fixed = (cents / 100).toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);

    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  // The standard Shopify money formatter. The shop's format string names which
  // convention it wants; everything outside the {{ }} placeholder (currency
  // symbols, codes, spacing) is preserved exactly as the merchant set it.
  function formatMoney(cents, format) {
    const match = format.match(PLACEHOLDER);
    const token = match ? match[1] : 'amount';
    let value;

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(PLACEHOLDER, value);
  }

  // The add-ons and the quantity selector live in the same product info column.
  // Scoping to it keeps a second product on the page (a featured product,
  // say) from driving this one's add-ons.
  function scopeFor(element) {
    return element.closest('product-info') || element.closest('.product__info-container') || document;
  }

  function render(scope, quantity) {
    scope.querySelectorAll(PRICE_SELECTOR).forEach((price) => {
      const unit = parseInt(price.dataset.addonPrice, 10);
      if (isNaN(unit)) return;

      const container = price.closest('.product-addons');
      const format = container && container.dataset.moneyFormat;

      // Without a usable format string, leave Liquid's rendering alone rather
      // than inventing one - a wrong currency string is worse than a unit price.
      if (!format || !PLACEHOLDER.test(format)) return;

      if (price.dataset.addonUnitLabel === undefined) {
        price.dataset.addonUnitLabel = price.textContent.trim();
      }

      price.textContent =
        quantity > 1 ? formatMoney(unit * quantity, format) : price.dataset.addonUnitLabel;
    });
  }

  function update(element) {
    const scope = scopeFor(element);
    const input = scope.querySelector(QUANTITY_SELECTOR);
    if (!input) return;

    const quantity = parseInt(input.value, 10);
    render(scope, isNaN(quantity) ? 1 : quantity);
  }

  function onQuantityChange(event) {
    const input = event.target;
    if (!input.matches || !input.matches(QUANTITY_SELECTOR)) return;
    update(input);
  }

  document.addEventListener('change', onQuantityChange);
  document.addEventListener('input', onQuantityChange);

  // Catches a quantity the browser restored on a back-navigation, which fires
  // no input or change event of its own.
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(PRICE_SELECTOR).forEach(update);
  });
})();
